import { useCallback, useRef } from "react";

import { METADATA_TYPE } from "app/components/widget/widget-types/EngagedVisitsSupportGraphWidget";
import { percentageSignFilter, timeFilter } from "app/filters/ngFilters";
import { useHDBannerCta } from "app/marketing/components/common/banners/HDBanner/useHDBannerCta";
import {
    PopTooltip,
    PopTooltipSingle,
} from "app/pages/website-analysis/TrafficAndEngagement/ChartTooltips/ChartTooltips";
import { CompareModeTooltip } from "app/pages/website-analysis/TrafficAndEngagement/ChartTooltips/CommonChartTooltip";
import { GenericGraph } from "app/pages/website-analysis/TrafficAndEngagement/Components/GenericGraph";
import { PopSingleModeLegends } from "app/pages/website-analysis/TrafficAndEngagement/Legends/PopSingleModeLegends";
import {
    tooltipPositioner,
    POPAndCompareTooltipPositioner,
} from "app/pages/website-analysis/TrafficAndEngagement/MD/chartConfig";
import {
    parseData,
    parsePOPCompareModeDataWithMTD,
    parsePOPSingleModeDataWithMTD,
} from "app/pages/website-analysis/TrafficAndEngagement/UtilityFunctionsAndConstants/ParseServerData";
import {
    dateFormatter,
    getChartType,
    getGetExcel,
    getrInitPOPItemsSingleModeWebSources,
    isTimeGranularityDisabled,
    isTimeGranularityDisabledOnlyMonth,
    neverStacking,
    OVERVIEW_EXCEL_ENDPOINT,
    POP_COMPARE_EXCEL_END_POINT,
    POP_SINGLE_CHART_TYPE,
} from "app/pages/website-analysis/TrafficAndEngagement/UtilityFunctionsAndConstants/UtilityFunctions";
import { ChartIds } from "pro-features/components/Chart/src/components/annotations/Helpers/ChartIdsHelper";

const ENDPOINT_METRIC = "Duration";

export const AvgVisitDuration = (props) => {
    const {
        name,
        getChartTooltipHeaders,
        meta: { isSingleMode, isPOP },
    } = props;

    const hdBannerCtaState = useHDBannerCta({ trackingName: "Graph tooltip/Engagement over time" });
    /*
     * The add-on availability arrives after the chart config is built, and the tooltip formatter lives
     * on in that config, so it has to read the state when the tooltip opens rather than close over it
     */
    const hdBannerCtaStateRef = useRef(hdBannerCtaState);
    hdBannerCtaStateRef.current = hdBannerCtaState;
    const getHdBannerCtaState = useCallback(() => hdBannerCtaStateRef.current, []);

    let metricData;
    const timeFormatter = ({ value }) => timeFilter()(value, null);
    if (!isPOP) {
        const addToDashboardName = "EngagementAvgVisitDuration";
        const getChartType = () => "line";
        const getExcelLink = getGetExcel(OVERVIEW_EXCEL_ENDPOINT);
        const getGraphData = parseData;
        const chartTooltip = (props) => (
            <CompareModeTooltip
                {...props}
                tableHeaders={getChartTooltipHeaders(props.timeGranularity)}
                xFormatter={dateFormatter}
                yFormatter={timeFormatter}
            />
        );
        metricData = {
            isTimeGranularityDisabled,
            getChartType,
            yAxisLabelsFormatter: timeFormatter,
            getExcelLink,
            addToDashboardName,
            chartType: METADATA_TYPE,
            getGraphData,
            chartTooltip,
            tooltipPositioner,
        };
    } else if (isSingleMode) {
        const addToDashboardName = "EngagementAvgVisitDuration";
        const getGraphData = parsePOPSingleModeDataWithMTD;
        const yAxisLabelsFormatter = timeFormatter;
        const getExcelLink = getGetExcel(OVERVIEW_EXCEL_ENDPOINT);
        const getChartType = () => "line";
        const chartType = POP_SINGLE_CHART_TYPE;
        const isStacking = () => false;
        const getWebSourceForApi = ({ webSource }) => webSource;
        const getSites = (props) => getrInitPOPItemsSingleModeWebSources(props);
        const legends = (props) => <PopSingleModeLegends {...props} />;
        const chartTooltip = (props) => (
            <PopTooltipSingle
                pointsData={props.points}
                xFormatter={dateFormatter}
                yFormatter={timeFormatter}
            />
        );
        metricData = {
            isTimeGranularityDisabled: isTimeGranularityDisabledOnlyMonth,
            getChartType,
            yAxisLabelsFormatter,
            getExcelLink,
            getGraphData,
            getWebSourceForApi,
            hideLegendsData: true,
            getSites,
            legends,
            chartTooltip,
            isStacking,
            addToDashboardName,
            chartType,
            tooltipPositioner,
        };
    } else {
        const yAxisLabelsFormatter = ({ value }) => percentageSignFilter()(value, 0);
        const getExcelLink = getGetExcel(POP_COMPARE_EXCEL_END_POINT);
        const getGraphData = parsePOPCompareModeDataWithMTD;
        const chartTooltip = (props) => (
            <PopTooltip
                pointsData={props.points}
                xFormatter={dateFormatter}
                yFormatter={timeFormatter}
            />
        );
        const popApiResponseName = "AvgVisitDuration";
        metricData = {
            isTimeGranularityDisabled: isTimeGranularityDisabledOnlyMonth,
            getChartType,
            yAxisLabelsFormatter,
            getExcelLink,
            getGraphData,
            chartTooltip,
            popApiResponseName,
            isStacking: neverStacking,
            hideLegendsData: true,
            tooltipPositioner: POPAndCompareTooltipPositioner,
        };
    }
    metricData.endpointMetric = ENDPOINT_METRIC;
    metricData.name = name;
    metricData.getHdBannerCtaState = getHdBannerCtaState;
    return (
        <GenericGraph
            metric={metricData}
            {...props}
            chartIdForAnnotations={`${ChartIds["TrafficAndEngagementAvgVisitDuration"]}`}
        />
    );
};
